import { useDispatch, useSelector } from 'react-redux'
import { selectActiveWidget, selectLayout, selectLayoutPanes, selectLayoutWidgets, selectSyncSettings } from '../../selectors/widgetDashboard'
import { DashboardEventData, DashboardSyncEvents, DashboardSyncSettings, IWidget, WidgetConfig, WidgetDashboardProps } from './widgetDashboard.interface'
import MultiPaneLayout from '../multiPane/multiPaneLayout/multiPaneLayout'
import { addWidget, deleteWidget, setActiveWidget, updateLayoutPanes, updateWidgets } from '../../reducer/widgetDashboard.reducer'
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'throttle-debounce'
import { IMultiPaneLayout, MultiPaneItem, MultiPanesConfig } from '../multiPane/multiPaneLayout.interface'
import SlotContainer from '../multiPane/slotContainer'
import { findActivePaneOnDelete, generatePaneLayoutsMap, getDefaultPane } from '../multiPane/multipPaneLayout.helper'
import { WidgetTypes } from './widgetDashboard.enums'
import { getDefaultWidget, getWidgetComponent } from './widgetDashboard.helper'
import { getSecurityKey } from '../../util/security'
import { AppContext } from '../../App'
import { selectActiveChartId, selectChartConfigs } from '../../selectors/chart'
import { updateChartConfig } from '../../reducer/chart.reducer'
import store from '../../store'
import cloneDeep from 'lodash/cloneDeep'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import { setMultiPaneFullScreen, resetWidgetFullscreen } from '../../actions/common.actions'
import { FullScreenToggle } from '../../selectors/common'
import { SuperChartConfiguration_TimeBucket } from '../../proto/generated/superchart-page'
import { ActionType, useUndoRedoContext } from '../UndoRedoFunctonalities/UndoRedo.context'
import { useTheme } from '@mui/material'
import DashboardPaneBackground from '../common/DashboardPaneBackground'
import {
    setDashboardFocusedSlot,
    setDashboardPaneHighlightEnabled,
    syncDashboardFocusedSlotFromRedux
} from './dashboardFocusedSlotStore'

const DUMMY_PANE = getDefaultPane()

const WidgetDashboard: React.FC<WidgetDashboardProps> = ({ id, allowInitialEmpty = true, maxPanes, tradeOptions, disableSplit }) => {
    const { push, undo, redo } = useUndoRedoContext()
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const activeChartId = useSelector(selectActiveChartId)
    const chart = appContext.getChart(activeChartId)
    const layout = useSelector(selectLayout(id))
    const panes = useSelector(selectLayoutPanes(id))
    const widgets = useSelector(selectLayoutWidgets(id))
    const widgetRefs = useRef<Record<number, IWidget>>({})
    const paneLayoutRef = useRef<IMultiPaneLayout>(null)
    const activeWidget = useSelector(selectActiveWidget(id))
    const activeWidgetRef = useRef(activeWidget)
    const previousWidgets = useRef<Record<number, WidgetConfig<any>>>({})
    const syncSettings = useSelector(selectSyncSettings(id))
    const dashboardSyncSettings = useRef<DashboardSyncSettings>(syncSettings)
    const mounted = useRef(false)
    const panesConfig = useMemo(() => (panes ? generatePaneLayoutsMap(panes) : {}), [panes])
    const isWidgetMaximized = useSelector(FullScreenToggle)
    const layoutStructureSignature = useMemo(
        () => JSON.stringify({ panes, widgetIds: Object.keys(widgets ?? {}).sort() }),
        [panes, widgets]
    )
    const isPlaceholderMode = !layout
    const effectivePanes = panes ?? DUMMY_PANE
    const effectivePanesConfig = useMemo(
        () => (panes ? panesConfig : generatePaneLayoutsMap(DUMMY_PANE)),
        [panes, panesConfig]
    )
    const previousKey = useRef(activeWidget?.settings?.key || '')
    const [hoveredPane, setHoveredPane] = useState<number>(NaN)
    const [isFullScreen, setIsFullScreen] = useState(false)
    const hasPushedRef = useRef(false)
    const visiblePanes = useMemo(() => Object.values(effectivePanesConfig).filter((pane) => pane.panes.length === 0), [effectivePanesConfig])
    const allowSplit = useMemo(() => !disableSplit && !isPlaceholderMode && (!maxPanes || visiblePanes.length < maxPanes), [isPlaceholderMode, visiblePanes, maxPanes, disableSplit])
    useEffect(() => {
        setDashboardPaneHighlightEnabled(visiblePanes.length > 1)
    }, [visiblePanes.length])
    const theme = useTheme()
    useEffect(() => {
        if (!hasPushedRef.current && layout) {
            push(layout, 'Initial Data', ActionType.UPDATE_LAYOUT)
            hasPushedRef.current = true
        }
    }, [layout])
    const activeChartIdRef = useRef(activeChartId)
    useEffect(() => {
        activeChartIdRef.current = activeChartId
    }, [activeChartId])
    useEffect(() => {
        activeWidgetRef.current = activeWidget
        syncDashboardFocusedSlotFromRedux(activeWidget?.slotId ?? null)
    }, [activeWidget])

    useEffect(() => {
        if (!isWidgetMaximized) return
        dispatch(resetWidgetFullscreen())
    }, [layoutStructureSignature, dispatch])
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isInFullScreen = !!document.fullscreenElement
            setIsFullScreen(isInFullScreen)
            dispatch(setMultiPaneFullScreen(isInFullScreen))
            if (!isInFullScreen) {
                setTimeout(() => {
                    const element = document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID)
                    if (element) {
                        element.style.removeProperty('height')
                        element.style.removeProperty('overflow')
                    }
                    ;(chart as any)?.resize?.()
                }, 100)
            }
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
        }
    }, [dispatch, chart])
    useEffect(() => {
        previousWidgets.current = {}
        mounted.current = false
        previousKey.current = ''
        appContext.setDashboard({
            getActiveWidget() {
                return activeWidgetRef.current
            },
            openActiveWidgetSettings() {
                const slotId = activeWidgetRef.current?.slotId
                if (slotId === undefined) return false
                const widget = widgetRefs.current[slotId]
                if (!widget?.openSettings) return false
                widget.openSettings()
                return true
            },
            splitPane(paneId, numberOfDivisions, horizontal) {
                paneLayoutRef.current && paneLayoutRef.current.splitPane(paneId, numberOfDivisions, horizontal)
            },
            deletePane(paneId) {
                paneLayoutRef.current && paneLayoutRef.current.deletePane(paneId)
            },
            updateWidgetConfig(slotId, settings) {
                const widget = previousWidgets.current[slotId]

                dispatch(
                    updateWidgets({
                        id,
                        widgets: {
                            ...previousWidgets.current,
                            [slotId]: {
                                ...widget,
                                settings
                            }
                        }
                    })
                )
            },
            updateSecurity(key, expressionMode, securities, displayName?: string) {
                if (previousKey.current !== key && activeWidgetRef.current && activeWidgetRef.current.settings.key !== key && widgetRefs.current[activeWidgetRef.current.slotId]) {
                    widgetRefs.current[activeWidgetRef.current.slotId]?.updateSettings({
                        key,
                        expressionMode,
                        securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                        displayName
                    })
                }
            },
            broadcast(id, event, data, forced = false) {
                Object.keys(widgetRefs.current).forEach((wId) => {
                    const widgetId = parseInt(wId)
                    if (widgetId === id) return
                    const widget = widgetRefs.current[widgetId]
                    if (!widget) return
                    switch (event) {
                        case DashboardSyncEvents.SECURITY:
                            if (dashboardSyncSettings.current.security || forced) {
                                widget.onSecurityUpdate && widget.onSecurityUpdate(data as DashboardEventData[DashboardSyncEvents.SECURITY])
                            }
                            break
                        case DashboardSyncEvents.CROSSHAIR:
                            if (dashboardSyncSettings.current.crosshair || forced) {
                                widget.onCrosshairUpdate && widget.onCrosshairUpdate(data as DashboardEventData[DashboardSyncEvents.CROSSHAIR])
                            }
                            break
                        case DashboardSyncEvents.TIME:
                            if (dashboardSyncSettings.current.time || forced) {
                                widget.onTimeUpdate && widget.onTimeUpdate(data as DashboardEventData[DashboardSyncEvents.TIME])
                            }
                            break
                        case DashboardSyncEvents.DATE:
                            if (dashboardSyncSettings.current.time || forced) {
                                widget.onDateUpdate && widget.onDateUpdate(data as DashboardEventData[DashboardSyncEvents.DATE])
                            }
                            break
                        case DashboardSyncEvents.TIME_BUCKET:
                            if (dashboardSyncSettings.current.timeBucket || forced) {
                                widget.onTimeBucketUpdate && widget.onTimeBucketUpdate(data as DashboardEventData[DashboardSyncEvents.TIME_BUCKET])
                            }
                            break
                        case DashboardSyncEvents.DRAWING:
                            if (dashboardSyncSettings.current.drawing || forced) {
                                widget.onDrawingUpdate && widget.onDrawingUpdate(data as DashboardEventData[DashboardSyncEvents.DRAWING])
                            }
                            break
                    }
                })
            }
        })
    }, [id])

    const focusWidget = useCallback(
        (slotId: number) => {
            const current = activeWidgetRef.current
            if (
                current?.slotId === slotId &&
                activeChartIdRef.current === current.settings.chartId &&
                mounted.current
            ) {
                return
            }

            const prevSlotId = current?.slotId
            setDashboardFocusedSlot(slotId)

            requestAnimationFrame(() => {
                dispatch(setActiveWidget({ id, slotId }))
                requestAnimationFrame(() => {
                    if (prevSlotId !== undefined && prevSlotId !== slotId) {
                        widgetRefs.current[prevSlotId]?.onBlur()
                    }
                    widgetRefs.current[slotId]?.onActive()
                })
            })
        },
        [dispatch, id]
    )

    const onPaneDeleted = useCallback(
        (slotId: number, oldPanesConfig: MultiPanesConfig) => {
            widgetRefs.current[slotId].onRemove()
            const newActiveId = findActivePaneOnDelete(slotId, oldPanesConfig)
            focusWidget(newActiveId)
            dispatch(
                deleteWidget({
                    id,
                    slotId
                })
            )
        },
        [focusWidget, dispatch, id]
    )

    useEffect(() => {
        if (!syncSettings || !dashboardSyncSettings.current || !activeWidget) return
        const prevSettings = { ...dashboardSyncSettings.current }
        const widget = widgetRefs.current[activeWidget?.slotId]
        if (!widget) return
        dashboardSyncSettings.current = syncSettings

        if (syncSettings.security && !prevSettings.security) {
            widget.onSyncSettingsChanged('security', syncSettings.security)
        }
        if (syncSettings.timeBucket && !prevSettings.timeBucket) {
            widget.onSyncSettingsChanged('timeBucket', syncSettings.timeBucket)
        }
    }, [syncSettings, appContext, activeWidget])

    const onPaneAdded = useCallback(
        (slotId: number, _oldPanesConfig?: MultiPanesConfig, _newPanesConfig?: MultiPanesConfig, sourcePaneId?: number) => {
            const sourceWidget =
                (sourcePaneId != null ? previousWidgets.current[sourcePaneId] : undefined) ??
                activeWidgetRef.current
            if (!sourceWidget) return

            if (!allowInitialEmpty) {
                const settings = sourceWidget.settings
                const sourceChartId = settings.chartId
                let timeBucket: SuperChartConfiguration_TimeBucket | undefined = settings.timeBucket
                if (sourceWidget.widgetType === WidgetTypes.CHART) {
                    const currentChart = appContext.getChart(sourceChartId)
                    timeBucket = currentChart?.chartConfig?.timeBucket ?? timeBucket
                } else {
                    const sourceConfig = selectChartConfigs(store.getState())[sourceChartId]
                    timeBucket = sourceConfig?.timeBucket ?? timeBucket
                }
                const widget = getDefaultWidget(
                    id,
                    slotId,
                    sourceWidget.widgetType,
                    settings.key,
                    settings.expressionMode,
                    settings.securities,
                    timeBucket
                )
                dispatch(
                    addWidget({
                        id,
                        widget
                    })
                )
                const sourceConfig = sourceChartId ? selectChartConfigs(store.getState())[sourceChartId] : undefined
                if (sourceConfig) {
                    dispatch(updateChartConfig({
                        viewKey: widget.settings.chartId,
                        config: cloneDeep(sourceConfig),
                    }))
                }
            }
        },
        [allowInitialEmpty, dispatch, id, appContext]
    )

    useEffect(() => {
        const previousWidgetIds = Object.keys(previousWidgets.current)
            .map((v) => parseInt(v))
            .sort((a, b) => a - b)
        const currentWidgetIds = Object.keys(widgets || {})
            .map((v) => parseInt(v))
            .sort((a, b) => a - b)

        if (!layout || currentWidgetIds.length === 0) {
            previousWidgets.current = widgets
            return
        }
        if (!mounted.current) {
            previousWidgets.current = widgets
            const activeSlotId = activeWidgetRef.current?.slotId
            const slotToFocus =
                activeSlotId !== undefined && widgets[activeSlotId] ? activeSlotId : currentWidgetIds[0]
            focusWidget(slotToFocus)
            mounted.current = true
            return
        }

        if (currentWidgetIds.length !== previousWidgetIds.length) {
            const [activeSlot] = currentWidgetIds.slice(-1)
            focusWidget(activeSlot)
        }
        previousWidgets.current = widgets
    }, [focusWidget, widgets, layout])

    const updateStoreLayout = useCallback(
        debounce(50, (newLayout: MultiPaneItem) => {
            dispatch(updateLayoutPanes({ id, panes: newLayout }))
        }),
        [id]
    )

    const resetHoveredPane = useCallback(() => {
        setHoveredPane(NaN)
    }, [])

    const widgetNodes: ReactNode[] = useMemo(() => {
        return Object.values(effectivePanesConfig).map((pane) => {
            const { id: paneId, panes: childPanes } = pane
            const widgetConfig = widgets[paneId]
            if (childPanes.length) return null
            return (
                <SlotContainer paneId={paneId} key={paneId}>
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            background: theme.palette.background.paper
                        }}
                        className="disable-selection"
                        onPointerDownCapture={() => {
                            focusWidget(paneId)
                        }}
                        onMouseEnter={() => setHoveredPane(Number.isFinite(widgetConfig?.slotId) ? widgetConfig?.slotId : paneId)}
                    >
                        {widgetConfig ? (
                            <div key={`${paneId}-${widgetConfig.widgetType}`} style={{ width: '100%', height: '100%' }}>
                                {getWidgetComponent(id, widgetConfig, widgetRefs.current, hoveredPane, allowSplit, tradeOptions)}
                            </div>
                        ) : isPlaceholderMode ? <DashboardPaneBackground /> : null}
                    </div>
                </SlotContainer>
            )
        })
    }, [effectivePanesConfig, widgets, id, focusWidget, hoveredPane, allowSplit, tradeOptions, isPlaceholderMode, theme])
    if (layout && Object.values(panesConfig).length && !Object.values(widgets).length && !allowInitialEmpty) {
        return <DashboardPaneBackground />
    }

    if (!id) {
        return <DashboardPaneBackground />
    }

    return (
        <div
            id={MULTIPANE_FULLSCREEN_MODE_ID}
            style={{
                height: isFullScreen ? '100vh' : '100%',
                flex: 1,
                minHeight: 0,
                overflow: isFullScreen ? 'visible' : 'hidden',
                display: 'flex',
                flexDirection: 'column',
                background: theme.palette.background.paper
            }}
        >
                    <MultiPaneLayout
                        layout={effectivePanes}
                        maxPanes={maxPanes}
                        onLayoutChange={isPlaceholderMode ? () => {} : updateStoreLayout}
                        ref={paneLayoutRef}
                        hidePaneActionButtons
                        onPaneAdded={onPaneAdded}
                        onMouseLeave={resetHoveredPane}
                        onPaneDeleted={onPaneDeleted}
                    >
                        {widgetNodes}
                    </MultiPaneLayout>
                </div>
    )
}

export default WidgetDashboard
